import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import type { ClientAccessDto, OAuthClientDto } from "@platform/core";
import { Alert, Badge, Button, Skeleton, Tabs, useToast } from "@platform/ui";
import { isApiError } from "../../api/errors";
import { useApi, useConsole } from "../../context";
import { useT } from "../../i18n";
import { CopyButton } from "../../lib/copy";
import { errorText } from "../../lib/errors";
import { ConfirmDialog } from "../../layout/ConfirmDialog";
import { ErrorBanner, PageCard } from "../../layout/Page";
import { isConsoleClient, useGroups, useUserTypes } from "../../lib/access";
import { AccessEditor, accessProblem, cleanAccess } from "./AccessEditor";
import { BrandingTab } from "./BrandingTab";
import { ClientForm, cleanUris, toScopes, type ClientFormValue } from "./ClientForm";
import { SecretDialog } from "./SecretDialog";
import { SNIPPET_LABELS, snippet, type SnippetLang } from "./snippets";

function toForm(c: OAuthClientDto): ClientFormValue {
  return {
    name: c.name,
    type: c.type,
    redirectUris: c.redirectUris.length ? c.redirectUris : [""],
    postLogoutRedirectUris: c.postLogoutRedirectUris,
    allowedScopes: c.allowedScopes,
    firstParty: c.firstParty,
    backchannelLogoutUri: c.backchannelLogoutUri ?? "",
    ownerTeam: c.ownerTeam ?? "",
    description: c.description ?? "",
  };
}

function SettingsTab({ client }: { client: OAuthClientDto }) {
  const { t } = useT();
  const api = useApi();
  const { can } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState(() => toForm(client));
  const save = useMutation({
    mutationFn: () =>
      api.clients.update(client.clientId, {
        name: form.name.trim(),
        redirectUris: cleanUris(form.redirectUris),
        postLogoutRedirectUris: cleanUris(form.postLogoutRedirectUris),
        allowedScopes: toScopes(form.allowedScopes),
        firstParty: form.firstParty,
        backchannelLogoutUri: form.backchannelLogoutUri.trim() || null,
        ownerTeam: form.ownerTeam.trim() || null,
        description: form.description.trim() || null,
      }),
    onSuccess: (c) => {
      qc.setQueryData(["client", client.clientId], c);
      void qc.invalidateQueries({ queryKey: ["clients"] });
      toast({ title: t("clients.saved"), tone: "success" });
    },
  });
  const editable = can("clients:write");
  return (
    <fieldset disabled={!editable} className="px-6 py-5">
      {save.isError && (
        <Alert tone="error" className="mb-4">
          {errorText(save.error)}
        </Alert>
      )}
      <ClientForm
        value={form}
        onChange={setForm}
        isNew={false}
        {...(isApiError(save.error) ? { fieldErrors: save.error.fieldErrors() } : {})}
      />
      {editable && (
        <div className="flex justify-end">
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            {t("common.save")}
          </Button>
        </div>
      )}
    </fieldset>
  );
}

function AccessTab({ client }: { client: OAuthClientDto }) {
  const { t } = useT();
  const api = useApi();
  const { can, config } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const types = useUserTypes();
  const groups = useGroups();
  const [access, setAccess] = useState<ClientAccessDto>(client.access);
  const managed = isConsoleClient(client.clientId, config.clientId);
  const editable = can("clients:write") && !managed;
  const save = useMutation({
    mutationFn: () => api.clients.setAccess(client.clientId, cleanAccess(access)),
    onSuccess: (c) => {
      qc.setQueryData(["client", client.clientId], c);
      void qc.invalidateQueries({ queryKey: ["clients"] });
      toast({ title: t("clients.saved"), tone: "success" });
    },
  });
  const problem = accessProblem(access, types.data?.items ?? []);
  return (
    <div className="px-6 py-5">
      {managed ? (
        <Alert tone="info" className="mb-4">
          {t("access.consoleManaged")}
        </Alert>
      ) : (
        <p className="mb-4 text-sm text-ink-2">{t("access.revokeHint")}</p>
      )}
      {save.isError && (
        <Alert tone="error" className="mb-4">
          {errorText(save.error)}
        </Alert>
      )}
      <AccessEditor
        value={access}
        onChange={setAccess}
        disabled={!editable}
        types={types.data?.items}
        groups={groups.data?.items}
      />
      {editable && (
        <div className="flex justify-end">
          <Button
            data-testid="save-access-button"
            loading={save.isPending}
            disabled={problem !== null}
            onClick={() => save.mutate()}
          >
            {t("common.save")}
          </Button>
        </div>
      )}
    </div>
  );
}

function IntegrationTab({ client }: { client: OAuthClientDto }) {
  const { t } = useT();
  const { config } = useConsole();
  const [lang, setLang] = useState<SnippetLang>("nextjs");
  const issuer = config.authServerUrl;
  const discovery = `${issuer}/.well-known/openid-configuration`;
  const code = snippet(lang, {
    issuer,
    clientId: client.clientId,
    redirectUri: client.redirectUris[0] ?? "https://app.example.com/callback",
    scopes: client.allowedScopes.join(" ") || "openid profile email",
    confidential: client.type === "confidential",
  });
  const row = (label: string, value: string) => (
    <div className="flex flex-wrap items-center gap-2 border-b border-line py-2 text-sm">
      <span className="w-36 text-ink-2">{label}</span>
      <code className="min-w-0 flex-1 font-mono text-xs break-all">{value}</code>
      <CopyButton value={value} />
    </div>
  );
  return (
    <div className="px-6 py-5">
      {row(t("clients.issuer"), issuer)}
      {row(t("clients.discovery"), discovery)}
      {row(t("clients.col.clientId"), client.clientId)}
      <div className="mt-5 flex flex-wrap gap-2" role="tablist" aria-label={t("clients.snippets")}>
        {(Object.keys(SNIPPET_LABELS) as SnippetLang[]).map((l) => (
          <Button
            key={l}
            role="tab"
            aria-selected={lang === l}
            size="sm"
            variant={lang === l ? "primary" : "secondary"}
            onClick={() => setLang(l)}
          >
            {SNIPPET_LABELS[l]}
          </Button>
        ))}
      </div>
      <div className="relative mt-3">
        <pre className="overflow-x-auto rounded-[var(--radius-control)] bg-[#0f1b2d] p-4 font-mono text-xs leading-relaxed text-white">
          {code}
        </pre>
        <div className="absolute top-2 right-2 rounded-full bg-white">
          <CopyButton value={code} />
        </div>
      </div>
    </div>
  );
}

export function ClientDetailPage() {
  const clientId = useParams({ strict: false }).clientId ?? "";
  const { t } = useT();
  const api = useApi();
  const { can } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const client = useQuery({ queryKey: ["client", clientId], queryFn: () => api.clients.get(clientId) });
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const refresh = (c: OAuthClientDto) => {
    qc.setQueryData(["client", clientId], c);
    void qc.invalidateQueries({ queryKey: ["clients"] });
  };
  const rotate = useMutation({
    mutationFn: () => api.clients.rotate(clientId),
    onSuccess: (r) => {
      setConfirmRotate(false);
      setSecret(r.clientSecret);
    },
  });
  const toggle = useMutation({
    mutationFn: (disable: boolean) =>
      disable ? api.clients.disable(clientId) : api.clients.enable(clientId),
    onSuccess: (c) => {
      refresh(c);
      toast({ title: t("clients.saved"), tone: "success" });
    },
  });
  return (
    <div className="mx-auto flex max-w-[var(--container-content)] flex-col gap-4">
      <Link
        to="/oauth-clients"
        className="inline-flex items-center gap-1 self-start text-sm text-primary-dark hover:underline"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {t("clients.back")}
      </Link>
      {client.isError ? (
        <PageCard>
          <ErrorBanner error={client.error} onRetry={() => void client.refetch()} />
        </PageCard>
      ) : client.isPending ? (
        <Skeleton className="h-40" />
      ) : (
        <>
          <PageCard>
            <div className="flex flex-wrap items-center gap-3 px-6 py-5">
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-medium">{client.data.name}</h2>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <code className="font-mono text-xs">{client.data.clientId}</code>
                  <CopyButton value={client.data.clientId} />
                  <Badge>
                    {client.data.type === "public"
                      ? t("clients.type.public")
                      : t("clients.type.confidential")}
                  </Badge>
                  {client.data.firstParty && <Badge tone="info">{t("clients.firstParty")}</Badge>}
                  <Badge dot tone={client.data.disabled ? "danger" : "success"}>
                    {client.data.disabled ? t("common.disabled") : t("common.enabled")}
                  </Badge>
                </div>
              </div>
              {can("clients:write") && (
                <div className="flex flex-wrap gap-2">
                  {client.data.type === "confidential" && (
                    <Button variant="secondary" onClick={() => setConfirmRotate(true)}>
                      {t("clients.rotate")}
                    </Button>
                  )}
                  <Button
                    variant={client.data.disabled ? "primary" : "outline"}
                    loading={toggle.isPending}
                    onClick={() => toggle.mutate(!client.data.disabled)}
                  >
                    {client.data.disabled ? t("clients.enable") : t("clients.disable")}
                  </Button>
                </div>
              )}
            </div>
            {toggle.isError && (
              <div className="px-6 pb-4">
                <Alert tone="error">{errorText(toggle.error)}</Alert>
              </div>
            )}
          </PageCard>
          <PageCard>
            <Tabs.Root defaultValue="settings">
              <Tabs.List label={t("nav.oauthClients")} className="px-2">
                <Tabs.Trigger value="settings">{t("clients.tab.settings")}</Tabs.Trigger>
                <Tabs.Trigger value="access">{t("clients.tab.access")}</Tabs.Trigger>
                <Tabs.Trigger value="branding">{t("clients.tab.branding")}</Tabs.Trigger>
                <Tabs.Trigger value="integration">{t("clients.tab.integration")}</Tabs.Trigger>
              </Tabs.List>
              <Tabs.Content value="settings">
                <SettingsTab key={JSON.stringify(client.data)} client={client.data} />
              </Tabs.Content>
              <Tabs.Content value="access">
                <AccessTab key={JSON.stringify(client.data.access)} client={client.data} />
              </Tabs.Content>
              <Tabs.Content value="branding">
                <BrandingTab key={JSON.stringify(client.data.branding ?? null)} client={client.data} />
              </Tabs.Content>
              <Tabs.Content value="integration">
                <IntegrationTab client={client.data} />
              </Tabs.Content>
            </Tabs.Root>
          </PageCard>
        </>
      )}
      <ConfirmDialog
        open={confirmRotate}
        onOpenChange={setConfirmRotate}
        title={t("clients.rotate")}
        confirmLabel={t("clients.rotate")}
        destructive
        loading={rotate.isPending}
        onConfirm={() => rotate.mutate()}
      >
        <p className="text-sm">{t("clients.rotateConfirm")}</p>
      </ConfirmDialog>
      <SecretDialog clientId={clientId} secret={secret} onClose={() => setSecret(null)} />
    </div>
  );
}
